import { useState, useEffect } from 'react'
import {
  Award,
  Download,
  Share2,
  CheckCircle,
  Calendar,
  Search
} from 'lucide-react'
import { generateCertificate } from '../../lib/certificateUtils'

interface Certificate {
  id: string
  courseName: string
  courseId: string
  studentName: string
  completionDate: string
  certificateNumber: string
  instructor: string
  grade?: string
  skills: string[]
}

export default function UserCertificates() {
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchCertificates()
  }, [])

  const fetchCertificates = async () => {
    try {
      const token = localStorage.getItem('token')
      const user = JSON.parse(localStorage.getItem('user') || '{}')

      // Fetch completed courses from enrollments
      const response = await fetch('/api/enrollments', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Filter out enrollments where course was deleted (null)
        const completedEnrollments = (data.enrollments || [])
          .filter((e: any) => e.completedAt && e.course !== null && e.course !== undefined)

        const certs = completedEnrollments.map((enrollment: any) => ({
          id: enrollment.id || enrollment._id,
          courseName: enrollment.course.title,
          courseId: enrollment.course.id || enrollment.course._id,
          studentName: user.name || 'Student',
          completionDate: enrollment.completedAt,
          certificateNumber: `SUDHA-${(enrollment.id || enrollment._id).substring(0, 8).toUpperCase()}`,
          instructor: enrollment.course.instructor || 'Sudha Beauty Expert',
          grade: 'Excellent',
          skills: enrollment.course.tags || []
        }))

        setCertificates(certs)
      }
    } catch (error) {
      console.error('Error fetching certificates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (certificate: Certificate) => {
    try {
      // Generate and download certificate PDF using the new Udemy-style design
      await generateCertificate({
        studentName: certificate.studentName,
        courseName: certificate.courseName,
        completionDate: new Date(certificate.completionDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        instructorName: certificate.instructor,
        certificateId: certificate.certificateNumber
      })
    } catch (error) {
      console.error('Error generating certificate:', error)
      alert('Failed to generate certificate. Please try again.')
    }
  }

  const handleShare = (certificate: Certificate) => {
    const shareText = `I just earned a certificate in ${certificate.courseName} from Sudha's Academy! 🎓`
    const shareUrl = `${window.location.origin}/certificates/${certificate.id}`

    if (navigator.share) {
      navigator.share({
        title: 'My Certificate',
        text: shareText,
        url: shareUrl
      }).catch(err => console.log('Error sharing:', err))
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
      alert('Certificate link copied to clipboard!')
    }
  }

  const filteredCertificates = certificates.filter(cert =>
    searchQuery === '' ||
    cert.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.certificateNumber.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-pink-200 dark:border-slate-800 rounded-full animate-spin border-t-pink-600 dark:border-t-pink-400"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Certificates</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1">
          Your achievements and certifications
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-500" />
        <input
          type="text"
          placeholder="Search certificates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 dark:bg-slate-700 rounded-lg">
              <Award className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Total</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {certificates.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 dark:bg-slate-700 rounded-lg">
              <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Completed</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {certificates.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 dark:bg-slate-700 rounded-lg">
              <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Latest</p>
              <p className="text-xs font-bold text-slate-900 dark:text-white">
                {certificates.length > 0
                  ? new Date(certificates[0].completionDate).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates List */}
      {filteredCertificates.length > 0 ? (
        <div className="space-y-4">
          {filteredCertificates.map((certificate) => (
            <div
              key={certificate.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 hover:shadow-lg transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
                    <Award className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                      {certificate.courseName}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        Completed on {new Date(certificate.completionDate).toLocaleDateString()}
                      </div>
                      <div className="hidden sm:block">•</div>
                      <div className="truncate">ID: {certificate.certificateNumber}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 md:ml-4 self-end md:self-auto">
                <button
                  onClick={() => handleDownload(certificate)}
                  className="p-2.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                  title="Download Certificate"
                >
                  <Download className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleShare(certificate)}
                  className="p-2.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                  title="Share Certificate"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <Award className="h-20 w-20 text-slate-300 dark:text-slate-600 mx-auto" />
          <h3 className="mt-4 text-xl font-semibold text-slate-800 dark:text-white">
            You haven't earned any certificates yet.
          </h3>
          <p className="mt-2 text-slate-500 dark:text-slate-400">
            Complete your courses to receive a certificate and showcase your new skills.
          </p>
          <button
            onClick={() => window.location.href = '/user/courses'}
            className="mt-6 px-5 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            Explore Courses
          </button>
        </div>
      )}
    </div>
  )
}